import axios from 'axios';
import config from '../Config';
import authHeader from './auth-header';
import { handle401Unauthorized } from '../utils/Helper';

const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

// Create a separate axios instance for Temper module to handle 401 errors
const temperAxiosInstance = axios.create();

// Add response interceptor to handle 401 errors for Temper module
temperAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handle401Unauthorized();
    }
    return Promise.reject(error);
  }
);

// Temper APIs
const getTemperList = (page, pagesize, search, ordering) => {
  return temperAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/temper/', {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getTemperById = id => {
  return temperAxiosInstance.get(`${API_URL}/api/${API_VERSION}/temper/${id}/`, {
    headers: authHeader(),
  });
};

const addTemper = formData => {
  return temperAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/temper/`,
    formData,
    { headers: authHeader() },
  );
};

const updateTemper = (id, formPayload) => {
  return temperAxiosInstance.put(
    `${API_URL}/api/${API_VERSION}/temper/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const patchTemper = (id, formPayload) => {
  return temperAxiosInstance.patch(
    `${API_URL}/api/${API_VERSION}/temper/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const deleteTemper = id => {
  return temperAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/temper/${id}/`,
    { headers: authHeader() },
  );
};

const getTemperDropdown = (filterKey) => {
  return temperAxiosInstance.get(`${API_URL}/api/${API_VERSION}/temper/dropdown/`, {
    headers: authHeader(),
    params: {
      ...(filterKey && { filter_key: filterKey }),
    },
  });
};

const getTemperArchiveList = (page, pagesize, search, ordering) => {
  return temperAxiosInstance.get(`${API_URL}/api/${API_VERSION}/temper/archived/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getTemperArchiveById = id => {
  return temperAxiosInstance.get(`${API_URL}/api/${API_VERSION}/temper-archive/${id}/`, {
    headers: authHeader(),
  });
};

const restoreTemper = ids => {
  return temperAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/temper/bulk-restore/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

const bulkArchiveTemper = ids => {
  return temperAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/temper/bulk-archive/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

// Bulk Import APIs
const bulkImportTemper = formData => {
  return temperAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/temper/bulk-import/`,
    formData,
    {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

const getImportLogs = (page, pagesize) => {
  return temperAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/temper/import-logs/`,
    {
      headers: authHeader(),
      params: {
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const getImportErrors = (importLogId, page, pagesize) => {
  return temperAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/temper/import-errors/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const downloadErrorReport = importLogId => {
  return temperAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/temper/error-report/download/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
      },
      responseType: 'blob',
    },
  );
};

const temperService = {
  getTemperList,
  getTemperById,
  addTemper,
  updateTemper,
  patchTemper,
  deleteTemper,
  getTemperDropdown,
  getTemperArchiveList,
  getTemperArchiveById,
  restoreTemper,
  bulkArchiveTemper,
  bulkImportTemper,
  getImportLogs,
  getImportErrors,
  downloadErrorReport,
};

export default temperService;
